import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Key, Trash2, Pencil, ShieldCheck, Lock, ShieldOff } from "lucide-react";
import { Pagination, usePagination } from "@/components/Pagination";

const PRIV_OPTIONS = [
  { v: "menu:performance", label: "Performance" },
  { v: "menu:timesheet", label: "Timesheet" },
  { v: "menu:projects", label: "Project" },
  { v: "menu:templates", label: "Template Activity" },
  { v: "menu:clients", label: "Client, Partner & PM" },
  { v: "menu:products", label: "Product" },
  { v: "read_only", label: "Read Only (Semua Fitur)", exclusive: true, description: "Hanya dapat melihat detail data. Tidak dapat menambah, mengedit, atau menghapus." },
];

function togglePriv(form, setForm) {
  return (v) => {
    const has = form.privileges.includes(v);
    const next = has
      ? form.privileges.filter((x) => x !== v)
      : [...form.privileges, v];
    setForm({ ...form, privileges: next });
  };
}

export default function Users() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [domains, setDomains] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [openEdit, setOpenEdit] = useState(null);
  const [openReset, setOpenReset] = useState(null);
  const [openDomains, setOpenDomains] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmBulk, setConfirmBulk] = useState(null); // "set" | "clear" | null
  const [newDomain, setNewDomain] = useState("");
  const [newForm, setNewForm] = useState({ email: "", password: "", name: "", role: "member", domain: "presales", privileges: [] });
  const [editForm, setEditForm] = useState({ name: "", role: "member", domain: "presales", privileges: [] });
  const [newPw, setNewPw] = useState("");

  const [pageItems, ctrl] = usePagination(items, 10);

  const load = async () => {
    const [u, d] = await Promise.all([api.get("/users"), api.get("/domains")]);
    setItems(u.data);
    setDomains(d.data);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", newForm);
      toast.success("User dibuat");
      setOpenNew(false);
      setNewForm({ email: "", password: "", name: "", role: "member", domain: "presales", privileges: [] });
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const doEdit = (it) => {
    setEditForm({ name: it.name, role: it.role, domain: it.domain || "presales", privileges: it.privileges || [] });
    setOpenEdit(it);
  };
  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/users/${openEdit.id}`, editForm);
      toast.success("Tersimpan");
      setOpenEdit(null);
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const confirmRemove = async () => {
    if (!confirmDelete) return;
    if (confirmDelete.id === user.id) { toast.error("Tidak dapat menghapus akun sendiri"); setConfirmDelete(null); return; }
    try {
      await api.delete(`/users/${confirmDelete.id}`);
      toast.success("Terhapus");
      setConfirmDelete(null);
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const reset = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/users/${openReset.id}/reset-password`, { new_password: newPw });
      toast.success("Password direset");
      setOpenReset(null);
      setNewPw("");
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const addDomain = async (e) => {
    e.preventDefault();
    const name = newDomain.trim().toLowerCase();
    if (!name) return;
    try {
      await api.post("/domains", { name });
      toast.success(`Domain "${name}" ditambahkan`);
      setNewDomain("");
      const { data } = await api.get("/domains");
      setDomains(data);
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const deleteDomain = async (id) => {
    try {
      await api.delete(`/domains/${id}`);
      toast.success("Domain dihapus");
      const { data } = await api.get("/domains");
      setDomains(data);
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const doBulk = async () => {
    const privs = confirmBulk === "set" ? ["read_only"] : [];
    try {
      const { data } = await api.post("/users/bulk-privileges", { privileges: privs, role_filter: "member" });
      toast.success(`Diterapkan ke ${data.modified} member`);
      setConfirmBulk(null);
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const DomainSelect = ({ value, onChange, testid }) => (
    <div className="flex gap-2">
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid}><SelectValue placeholder="Pilih domain" /></SelectTrigger>
        <SelectContent>
          {domains.map((d) => (
            <SelectItem key={d.id} value={d.name}>
              {d.name}{d.is_default ? " (default)" : ""}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button type="button" variant="outline" className="rounded-sm shrink-0"
        onClick={() => setOpenDomains(true)} data-testid="open-domains-button" title="Kelola domain">
        <Plus className="h-4 w-4" />
      </Button>
    </div>
  );

  const PrivilegeGroup = ({ form, setForm, testidPrefix }) => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
      {PRIV_OPTIONS.map((p) => {
        const checked = form.privileges.includes(p.v);
        return (
          <label
            key={p.v}
            className={`flex items-start gap-2 text-sm p-2 rounded-sm border ${
              checked ? (p.exclusive ? "border-amber-300 bg-amber-50" : "border-primary/40 bg-primary/5") : "border-border"
            }`}
          >
            <Checkbox
              checked={checked}
              onCheckedChange={() => togglePriv(form, setForm)(p.v)}
              data-testid={`${testidPrefix}-${p.v}`}
              className="mt-0.5"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="font-medium">{p.label}</span>
                {p.exclusive && <Lock className="h-3 w-3 text-amber-600" />}
              </div>
              {p.description && (
                <div className="text-[11px] text-muted-foreground mt-0.5 leading-snug">{p.description}</div>
              )}
            </div>
          </label>
        );
      })}
    </div>
  );

  return (
    <div className="space-y-8" data-testid="users-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="label-mini">User Management</div>
          <h1 className="mt-1 font-heading text-4xl font-black tracking-tight">Kelola Akses Tim</h1>
          <p className="text-sm text-muted-foreground mt-2">RBAC per domain user. Fitur ini hanya untuk Team Leader.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="rounded-sm" onClick={() => setConfirmBulk("set")} data-testid="bulk-set-readonly-button">
            <Lock className="h-4 w-4 mr-2" /> Set Semua Member Read Only
          </Button>
          <Button variant="outline" className="rounded-sm" onClick={() => setConfirmBulk("clear")} data-testid="bulk-clear-readonly-button">
            <ShieldOff className="h-4 w-4 mr-2" /> Cabut Read Only
          </Button>
          <Button variant="outline" className="rounded-sm" onClick={() => setOpenDomains(true)} data-testid="manage-domains-button">
            <ShieldCheck className="h-4 w-4 mr-2" /> Kelola Domain
          </Button>
          <Dialog open={openNew} onOpenChange={setOpenNew}>
            <DialogTrigger asChild>
              <Button className="rounded-sm" data-testid="new-user-button"><Plus className="h-4 w-4 mr-2" />User Baru</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>User Baru</DialogTitle></DialogHeader>
              <form onSubmit={create} className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2"><Label className="label-mini">Nama</Label>
                  <Input required value={newForm.name} onChange={(e) => setNewForm({ ...newForm, name: e.target.value })} data-testid="new-user-name" /></div>
                <div><Label className="label-mini">Email</Label>
                  <Input required type="email" value={newForm.email} onChange={(e) => setNewForm({ ...newForm, email: e.target.value })} data-testid="new-user-email" /></div>
                <div><Label className="label-mini">Password</Label>
                  <Input required type="password" value={newForm.password} onChange={(e) => setNewForm({ ...newForm, password: e.target.value })} data-testid="new-user-password" /></div>
              <div><Label className="label-mini">Role</Label>
                <Select value={newForm.role} onValueChange={(v) => setNewForm({ ...newForm, role: v })}>
                  <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="member">Member</SelectItem>
                    <SelectItem value="manager">Manager (read only)</SelectItem>
                    <SelectItem value="team_leader">Team Leader</SelectItem>
                  </SelectContent>
                </Select></div>
                <div>
                  <Label className="label-mini">Domain</Label>
                  <DomainSelect value={newForm.domain} onChange={(v) => setNewForm({ ...newForm, domain: v })} testid="new-user-domain" />
                </div>
                <div className="md:col-span-2">
                  <Label className="label-mini">Akses Menu</Label>
                  <PrivilegeGroup form={newForm} setForm={setNewForm} testidPrefix="new-priv" />
                </div>
                <Button type="submit" className="md:col-span-2 rounded-sm" data-testid="save-new-user">Simpan</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="card-tech p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-accent text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left px-6 py-3">Nama</th>
              <th className="text-left px-6 py-3">Email</th>
              <th className="text-left px-6 py-3">Role</th>
              <th className="text-left px-6 py-3">Domain</th>
              <th className="text-left px-6 py-3">Privileges</th>
              <th className="px-6 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {pageItems.map((u) => {
              const readonly = (u.privileges || []).includes("read_only");
              return (
                <tr key={u.id} className="border-t border-border" data-testid={`user-row-${u.id}`}>
                  <td className="px-6 py-3 font-medium">{u.name}</td>
                  <td className="px-6 py-3 mono text-xs">{u.email}</td>
                  <td className="px-6 py-3">
                    <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-sm bg-primary/10 text-primary">
                      {u.role}
                    </span>
                  </td>
                  <td className="px-6 py-3 text-muted-foreground">{u.domain || "-"}</td>
                  <td className="px-6 py-3 text-xs text-muted-foreground">
                    {readonly ? (
                      <span className="inline-flex items-center gap-1 font-bold text-amber-700">
                        <Lock className="h-3 w-3" /> Read Only
                      </span>
                    ) : (u.privileges || []).join(", ") || "-"}
                  </td>
                  <td className="px-6 py-3 text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={() => doEdit(u)} data-testid={`edit-user-${u.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => setOpenReset(u)} data-testid={`reset-user-${u.id}`}><Key className="h-4 w-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(u)} data-testid={`delete-user-${u.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <Pagination ctrl={ctrl} testidPrefix="users-pg" />
      </div>

      {/* Edit */}
      <Dialog open={!!openEdit} onOpenChange={(v) => !v && setOpenEdit(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit User</DialogTitle></DialogHeader>
          {openEdit && (
            <form onSubmit={saveEdit} className="space-y-3">
              <div><Label className="label-mini">Nama</Label>
                <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="edit-user-name" /></div>
              <div><Label className="label-mini">Role</Label>
                <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                  <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="member">Member</SelectItem>
                    <SelectItem value="manager">Manager (read only)</SelectItem>
                    <SelectItem value="team_leader">Team Leader</SelectItem>
                  </SelectContent>
                </Select></div>
              <div>
                <Label className="label-mini">Domain</Label>
                <DomainSelect value={editForm.domain} onChange={(v) => setEditForm({ ...editForm, domain: v })} testid="edit-user-domain" />
              </div>
              <div>
                <Label className="label-mini">Akses Menu</Label>
                <PrivilegeGroup form={editForm} setForm={setEditForm} testidPrefix="edit-priv" />
              </div>
              <Button type="submit" className="w-full rounded-sm" data-testid="save-edit-user">Simpan</Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Reset password */}
      <Dialog open={!!openReset} onOpenChange={(v) => !v && setOpenReset(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reset Password: {openReset?.email}</DialogTitle></DialogHeader>
          <form onSubmit={reset} className="space-y-3">
            <div><Label className="label-mini">Password Baru</Label>
              <Input required type="password" minLength={6} value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid="reset-password-input" /></div>
            <Button type="submit" className="w-full rounded-sm" data-testid="submit-reset-password">Reset Password</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Domain management */}
      <Dialog open={openDomains} onOpenChange={setOpenDomains}>
        <DialogContent>
          <DialogHeader><DialogTitle>Kelola Domain</DialogTitle></DialogHeader>
          <form onSubmit={addDomain} className="flex gap-2">
            <Input value={newDomain} onChange={(e) => setNewDomain(e.target.value)}
              placeholder="mis. sales, engineering" data-testid="new-domain-input" />
            <Button type="submit" className="rounded-sm" data-testid="add-domain-button"><Plus className="h-4 w-4 mr-1" />Tambah</Button>
          </form>
          <div className="mt-4 divide-y divide-border border border-border rounded-sm">
            {domains.map((d) => (
              <div key={d.id} className="flex items-center justify-between px-4 py-2 text-sm" data-testid={`domain-row-${d.id}`}>
                <div>
                  <span className="font-medium">{d.name}</span>
                  {d.is_default && (<span className="ml-2 text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Default</span>)}
                </div>
                {d.is_default ? (
                  <span className="text-[10px] tracking-widest uppercase font-bold text-muted-foreground inline-flex items-center gap-1"><Lock className="h-3 w-3" /> Terkunci</span>
                ) : (
                  <Button size="sm" variant="ghost" onClick={() => deleteDomain(d.id)} data-testid={`delete-domain-${d.id}`}><Trash2 className="h-4 w-4" /></Button>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete user confirmation */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Yakin hapus user ini?</AlertDialogTitle>
            <AlertDialogDescription>
              Anda akan menghapus <span className="font-semibold">{confirmDelete?.name}</span> ({confirmDelete?.email}).
              Aksi ini tidak dapat dibatalkan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-user">Batal</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={confirmRemove}
              data-testid="confirm-delete-user"
            >
              Ya, hapus
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk read-only confirmation */}
      <AlertDialog open={!!confirmBulk} onOpenChange={(v) => !v && setConfirmBulk(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmBulk === "set" ? "Set semua member menjadi Read Only?" : "Cabut Read Only dari semua member?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmBulk === "set"
                ? "Semua akun dengan role Member akan diberi privilege Read Only. Mereka hanya dapat melihat data, tidak dapat menambah/mengedit/menghapus."
                : "Semua akun Member akan kehilangan privilege Read Only. Privilege lainnya akan dikosongkan (bisa di-set ulang per user)."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-bulk">Batal</AlertDialogCancel>
            <AlertDialogAction onClick={doBulk} data-testid="confirm-bulk">Terapkan</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
